import { motion } from 'framer-motion';
import { Award, Users, FileText, Briefcase, Sparkles, Target, Rocket } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: Briefcase, value: '4+', label: 'Years Experience', color: 'blue' },
    { icon: Users, value: '500+', label: 'Connections', color: 'red' },
    { icon: Award, value: '20+', label: 'Certifications', color: 'blue' },
    { icon: FileText, value: '15+', label: 'Projects', color: 'red' },
  ];

  const highlights = [
    { icon: Sparkles, text: 'Innovation-Driven', color: 'blue' },
    { icon: Target, text: 'Results-Focused', color: 'red' },
    { icon: Rocket, text: 'Scalable Solutions', color: 'blue' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section
      id="about"
      className="bg-gradient-to-b from-black to-gray-900 px-4 py-20 sm:px-6 lg:px-8"
    >
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 className="mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-4xl font-bold text-transparent sm:text-5xl">
            About Me
          </h2>
          <div className="mx-auto h-1 w-24 bg-gradient-to-r from-blue-500 to-red-500"></div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="mx-auto max-w-4xl space-y-8 leading-relaxed">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] p-8 backdrop-blur-md"
            >
              <p className="text-xl text-gray-200">
                As an <span className="font-bold text-white">Executive Engineer</span> with a
                multidisciplinary background in{' '}
                <span className="text-blue-400">Software Engineering</span>,{' '}
                <span className="text-red-400">Gaming, Betting & Gambling</span>,{' '}
                <span className="text-blue-400">Artificial Intelligence</span>,{' '}
                <span className="text-red-400">Cloud Computing</span>, Architecture, Operating
                Systems, and Telecommunications, I bring a strategic and systems-level approach to
                solving complex challenges through technology.
              </p>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-4 text-lg text-gray-300"
              >
                <p>
                  I am currently pursuing a{' '}
                  <span className="font-semibold text-white">
                    Master of Science in Computer Science
                  </span>{' '}
                  at the University of Botswana, an{' '}
                  <span className="font-semibold text-white">Executive MBA</span> at Sheffield
                  Hallam University. These two programs reflect my commitment to bridging deep
                  technical expertise with high-level business and engineering leadership.
                </p>
                <p>
                  My work is rooted in designing and delivering intelligent, scalable systems, from
                  AI-powered applications and cloud-native platforms to secure telecommunications
                  solutions. I have extensive experience leveraging technologies in software
                  engineering & architecture, and advanced networking tools to build systems that
                  are scalable, resilient, compliant, and aligned with organizational goals.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="space-y-4 text-lg text-gray-300"
              >
                <p>
                  Beyond technical execution, I am deeply invested in{' '}
                  <span className="font-semibold text-blue-400">innovation and impact</span>. I am
                  building a tech startup focused on solving real-world problems using data-driven
                  and AI-based solutions tailored for emerging markets. My goal is to lead ventures
                  that combine technical depth with business intelligence to create scalable,
                  meaningful change.
                </p>
                <p>
                  I am also an active researcher and published academic, having supervised research
                  projects across undergraduate, and postgraduate levels. My academic and
                  professional experiences position me at the intersection of{' '}
                  <span className="font-semibold text-red-400">
                    innovation, leadership, and engineering excellence
                  </span>
                  .
                </p>
              </motion.div>
            </div>

            {/* Highlights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-4"
            >
              {highlights.map((highlight, index) => {
                const Icon = highlight.icon;
                return (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05, y: -3 }}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 ${
                      highlight.color === 'blue'
                        ? 'border-blue-500/30 bg-blue-500/10 text-blue-300'
                        : 'border-red-500/30 bg-red-500/10 text-red-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{highlight.text}</span>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-6 md:grid-cols-4"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                className="rounded-xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-md transition-all duration-300 hover:bg-white/10"
              >
                <div
                  className={`mb-4 inline-flex rounded-lg p-3 ${
                    stat.color === 'blue'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-3xl font-bold text-white">{stat.value}</h3>
                <p className="text-sm text-gray-400">{stat.label}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
